import React, { useMemo } from 'react';
import { PixelRuns, spriteRuns } from './PixelSprite';

const WIDTH = 80;
const HEIGHT = 60;

const BAYER = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5]
];

const seededRandom = (seed) => () => {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};

// A dithered sphere, lit from the upper right, merged into horizontal runs.
const planetRuns = ({ cx, cy, r, colors }) => {
  const runs = [];
  const light = [0.55, -0.6, 0.58];

  for (let y = Math.max(0, cy - r); y < Math.min(HEIGHT, cy + r); y += 1) {
    let current = null;
    for (let x = Math.max(0, cx - r); x <= Math.min(WIDTH, cx + r); x += 1) {
      const dx = (x + 0.5 - cx) / r;
      const dy = (y + 0.5 - cy) / r;
      const d2 = dx * dx + dy * dy;
      let fill = null;

      if (d2 <= 1 && x < WIDTH) {
        const dz = Math.sqrt(1 - d2);
        const shade = Math.max(0, dx * light[0] + dy * light[1] + dz * light[2]);
        const level = shade * (colors.length - 1) + BAYER[y % 4][x % 4] / 16 - 0.5;
        fill = colors[Math.max(0, Math.min(colors.length - 1, Math.round(level)))];
      }

      if (current && current.fill === fill) {
        current.width += 1;
      } else {
        if (current && current.fill) runs.push(current);
        current = { x, y, width: 1, fill };
      }
    }
    if (current && current.fill) runs.push(current);
  }

  return runs;
};

const ship = {
  palette: {
    k: 'var(--space-outline)',
    h: 'var(--space-hull)',
    s: 'var(--space-hull-shade)',
    w: 'var(--yellow)',
    b: 'var(--blue)',
    e: 'var(--space-hull-shade)'
  },
  rows: [
    '....................kkkkkkkkk',
    '...................khhhhwhhhhk',
    '..........kkkkkkkkkhhhhhhhhhhkkkkkkkkkkkkkkk',
    '.......kkkhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhkkkk',
    'kkkk.kkhhhwwhhwwhhwwhhwwhhwwhhwwhhwwhhwwhhhhhhhhkk',
    'keekkhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhkk',
    'keekssssssssssssssssssssssssssssssssssssssssssssssk',
    'kkkk.kksssssssssssssssssbbbbbbbbsssssssssssssssskk',
    '.......kkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkk'
  ]
};

const SHIP_X = 16;
const SHIP_Y = 22;

// Drones leave the launch bay on the ship's underside, one per initiative.
const drones = [
  { color: 'var(--yellow)', dx: 26, dy: 26, delay: 0 },
  { color: 'var(--red)', dx: 8, dy: 30, delay: 2.4 },
  { color: 'var(--blue)', dx: 38, dy: 12, delay: 4.8 }
];

const SpaceScene = () => {
  const stars = useMemo(() => {
    const random = seededRandom(7);
    return Array.from({ length: 36 }, (_, index) => ({
      x: Math.floor(random() * WIDTH),
      y: Math.floor(random() * HEIGHT),
      bright: random() > 0.78,
      twinkle: index % 5 === 0,
      delay: (random() * 4).toFixed(2)
    }));
  }, []);

  const planet = useMemo(
    () =>
      planetRuns({
        cx: 6,
        cy: 63,
        r: 24,
        colors: ['var(--space-planet-dark)', 'var(--space-planet-mid)', 'var(--space-planet-light)']
      }),
    []
  );

  const moon = useMemo(
    () =>
      planetRuns({
        cx: 67,
        cy: 10,
        r: 4,
        colors: ['var(--space-moon-dark)', 'var(--space-moon-light)']
      }),
    []
  );

  const shipRuns = useMemo(() => spriteRuns(ship.rows, ship.palette), []);

  return (
    <svg
      className="space-scene"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      shapeRendering="crispEdges"
      role="img"
      aria-label="Pixel-art starship launching three small drones past a planet"
    >
      <rect width={WIDTH} height={HEIGHT} style={{ fill: 'var(--space-void)' }} />

      {stars.map((star, index) => (
        <rect
          key={index}
          className={star.twinkle ? 'star star-twinkle' : 'star'}
          x={star.x}
          y={star.y}
          width="1"
          height="1"
          style={{
            fill: star.bright ? 'var(--space-star)' : 'var(--space-star-dim)',
            animationDelay: `${star.delay}s`
          }}
        />
      ))}

      <PixelRuns runs={moon} />
      <PixelRuns runs={planet} />

      {drones.map((drone) => (
        <g
          key={drone.color}
          className="drone"
          style={{
            '--dx': `${drone.dx}px`,
            '--dy': `${drone.dy}px`,
            animationDelay: `${drone.delay}s`
          }}
        >
          <rect x={SHIP_X + 27} y={SHIP_Y + 8} width="1" height="1" style={{ fill: 'var(--space-star-dim)' }} />
          <rect x={SHIP_X + 28} y={SHIP_Y + 8} width="2" height="2" style={{ fill: drone.color }} />
        </g>
      ))}

      <g className="ship">
        <rect className="ship-flame" x={SHIP_X - 4} y={SHIP_Y + 5} width="4" height="2" style={{ fill: 'var(--red)' }} />
        <rect className="ship-flame ship-flame-core" x={SHIP_X - 2} y={SHIP_Y + 5} width="2" height="2" style={{ fill: 'var(--yellow)' }} />
        <PixelRuns runs={shipRuns} offsetX={SHIP_X} offsetY={SHIP_Y} />
      </g>
    </svg>
  );
};

export default SpaceScene;
